import { db } from "./firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { authenticator } from "otplib";
import QRCode from "qrcode";

// Check if 2FA is required globally
export const isGlobalTwoFactorRequired = async () => {
  try {
    console.log("Checking global 2FA requirements");
    const settingsRef = doc(db, "Settings", "twoFactorSettings");

    try {
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        console.log("2FA settings document exists:", data);
        return data.required || false;
      }

      // If we get here, the document doesn't exist but we have read permissions
      console.log("2FA settings document doesn't exist, creating default settings");

      // Since we have read permission, we might have write permission
      // Let's try to create the document with 2FA enabled by default for security
      try {
        console.log("Attempting to create default 2FA settings (enabled for security)");
        await setDoc(settingsRef, {
          required: true, // Default to true for better security
          updatedAt: new Date().toISOString(),
          createdBy: "system",
          note: "Default setting - 2FA required for all users"
        });
        console.log("Default 2FA settings created successfully (required: true)");
        return true; // Return true since we just enabled it
      } catch (writeError) {
        console.error("Error creating default 2FA settings:", writeError);
        // If we can't write, return true for security (better to require 2FA than not)
        if (writeError.code === "permission-denied") {
          console.log(
            "Permission denied when creating settings, defaulting to required for security"
          );
        }
        return true; // Default to requiring 2FA for security
      }
    } catch (readError) {
      console.error("Error reading 2FA settings:", readError);
      if (readError.code === "permission-denied") {
        console.error("Permission denied when reading settings, defaulting to required for security");
        // For security, if we can't read the settings, require 2FA
        return true;
      }
      // For other errors, also default to requiring 2FA for security
      return true;
    }
  } catch (error) {
    console.error("Error in isGlobalTwoFactorRequired:", error);
    // For any error, default to requiring 2FA for security
    return true;
  }
};

// Set global 2FA requirement
export const setGlobalTwoFactorRequired = async (required) => {
  try {
    console.log("Setting global 2FA requirement to:", required);
    const settingsRef = doc(db, "Settings", "twoFactorSettings");

    try {
      await setDoc(
        settingsRef,
        {
          required: required,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      console.log("Global 2FA requirement updated successfully");
      return true;
    } catch (writeError) {
      console.error("Error updating 2FA settings:", writeError);
      if (writeError.code === "permission-denied") {
        console.error("Permission denied when updating settings");
        // Create a specific error with code to be handled by the caller
        const error = new Error(
          "You don't have permission to update 2FA settings"
        );
        error.code = "permission-denied";
        throw error;
      }
      throw writeError;
    }
  } catch (error) {
    console.error("Error in setGlobalTwoFactorRequired:", error);
    // Re-throw the error to be handled by the caller
    throw error;
  }
};

// Generate a 2FA secret for a user
export const generateTwoFactorSecret = async (userId, userEmail) => {
  try {
    const newSecret = authenticator.generateSecret();
    const userRef = doc(db, "users", userId);

    // Store the secret in Firestore
    await setDoc(
      userRef,
      {
        email: userEmail,
        twoFactorSecret: newSecret,
        twoFactorEnabled: true,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );

    // Generate QR code URL
    const otpauth = authenticator.keyuri(userEmail, "CreateVista", newSecret);
    return new Promise((resolve, reject) => {
      QRCode.toDataURL(otpauth, (err, imageUrl) => {
        if (err) {
          reject("Could not generate QR code");
          return;
        }
        resolve({
          secret: newSecret,
          qrCodeUrl: imageUrl,
        });
      });
    });
  } catch (error) {
    console.error("Error generating 2FA secret:", error);
    throw new Error("Failed to generate authentication secret");
  }
};

// Check if a user has 2FA enabled
export const isUserTwoFactorEnabled = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return userDoc.data().twoFactorEnabled || false;
    }

    return false;
  } catch (error) {
    console.error("Error checking user 2FA status:", error);
    return false;
  }
};

// Verify a 2FA token for a user
export const verifyTwoFactorToken = async (userId, token) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists() || !userDoc.data().twoFactorSecret) {
      throw new Error(
        "Two-factor authentication is not set up for this account"
      );
    }

    const secretToVerify = userDoc.data().twoFactorSecret;

    return authenticator.verify({
      token: token,
      secret: secretToVerify,
    });
  } catch (error) {
    console.error("Error verifying 2FA token:", error);
    throw new Error("Failed to verify token");
  }
};
